import { useEffect, useRef, useState, type FormEvent } from "react";
import { useAuth, type ReauthenticationRequest } from "../auth/context";
import { authErrorMessage } from "../auth/errors";
import { GithubMark, GoogleMark } from "./icons";

export function ReauthenticateModal({
  onClose,
  onSuccess,
}: {
  onClose: () => void;
  onSuccess: () => void;
}) {
  const { user, reauthenticate } = useAuth();
  const passwordInput = useRef<HTMLInputElement>(null);
  const onCloseRef = useRef(onClose);
  const mounted = useRef(true);
  const [submitting, setSubmitting] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const providerIds = new Set(user?.providerData.map(({ providerId }) => providerId));
  const hasPassword = providerIds.has("password") && !!user?.email;
  const hasGoogle = providerIds.has("google.com");
  const hasGithub = providerIds.has("github.com");
  const hasSupportedProvider = hasPassword || hasGoogle || hasGithub;

  function clearPassword() {
    if (passwordInput.current) passwordInput.current.value = "";
  }

  function close() {
    clearPassword();
    setLocalError(null);
    onClose();
  }

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    mounted.current = true;
    const passwordElement = passwordInput.current;
    function closeOnEscape(event: KeyboardEvent) {
      if (event.key === "Escape") {
        if (passwordElement) passwordElement.value = "";
        setLocalError(null);
        onCloseRef.current();
      }
    }
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      mounted.current = false;
      if (passwordElement) passwordElement.value = "";
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, []);

  async function attempt(request: ReauthenticationRequest) {
    setSubmitting(true);
    setLocalError(null);
    try {
      await reauthenticate(request);
      clearPassword();
      if (mounted.current) {
        onSuccess();
        onClose();
      }
    } catch (cause) {
      clearPassword();
      if (!mounted.current) return;
      const code = cause instanceof Error ? (cause as Error & { code?: string }).code : undefined;
      if (code === "auth/popup-closed-by-user" || code === "auth/cancelled-popup-request") {
        setLocalError(null);
      } else {
        setLocalError(authErrorMessage(cause, "Reauthentication failed. Please try again."));
      }
    } finally {
      clearPassword();
      if (mounted.current) setSubmitting(false);
    }
  }

  function submitPassword(event: FormEvent) {
    event.preventDefault();
    const password = passwordInput.current?.value ?? "";
    if (!password) return;
    void attempt({ providerId: "password", password });
  }

  return (
    <div className="modal-backdrop" onClick={close}>
      <section
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="reauthenticate-title"
        aria-describedby="reauthenticate-description"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="modal-header">
          <h2 className="modal-title" id="reauthenticate-title">
            Reauthenticate
          </h2>
          <button
            type="button"
            className="icon-button modal-close"
            aria-label="Close Reauthentication"
            onClick={close}
            autoFocus
          >
            &times;
          </button>
        </div>
        <p id="reauthenticate-description">
          Verify the current account before revealing connection details. You will press Reveal
          Connection Details again after verification.
        </p>
        <div className="auth-methods">
          {hasGoogle && (
            <button
              type="button"
              className="btn btn--ghost btn--block auth-provider"
              disabled={submitting}
              onClick={() => void attempt({ providerId: "google.com" })}
            >
              <GoogleMark size={18} />
              Reauthenticate With Google
            </button>
          )}
          {hasGithub && (
            <button
              type="button"
              className="btn btn--ghost btn--block auth-provider"
              disabled={submitting}
              onClick={() => void attempt({ providerId: "github.com" })}
            >
              <GithubMark size={18} />
              Reauthenticate With GitHub
            </button>
          )}
          {hasPassword && (
            <form className="form auth-email-form" onSubmit={submitPassword}>
              <div className="field">
                <label className="field-label" htmlFor="reauthenticate-password">
                  Password
                </label>
                <input
                  ref={passwordInput}
                  id="reauthenticate-password"
                  className="input"
                  type="password"
                  name="password"
                  autoComplete="current-password"
                  required
                  disabled={submitting}
                />
              </div>
              <button type="submit" className="btn btn--primary btn--block" disabled={submitting}>
                {submitting ? "Reauthenticating…" : "Reauthenticate With Email"}
              </button>
            </form>
          )}
          {!hasSupportedProvider && (
            <p role="alert">
              No supported sign-in method is linked to this account. Sign Out and sign in again.
            </p>
          )}
          {localError && <p role="alert">{localError}</p>}
        </div>
      </section>
    </div>
  );
}
